import { usePreloadedQuery } from 'react-relay';
import { PreloadedQuery } from 'react-relay/relay-hooks/EntryPointTypes';
import {
  getDiagnosticStatusByClassReport,
  getDiagnosticStatusByTargetGroupReport,
} from '../graphQLOperations/Diagnostics';
import { DiagnosticsStatusByClassReportQuery } from '../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';
import { DiagnosticsStatusByTargetGroupReportQuery } from '../graphQLOperations/__generated__/DiagnosticsStatusByTargetGroupReportQuery.graphql';
import { ClassDiagnosticStatusReport } from '../components/TeacherDashboard/Diagnostic/helper/chartHelper';

interface Params {
  isSiteAdmin: boolean;
  isFilteredByRoadmap: boolean;
  queryDiagnosticStatusByClassReportRef: PreloadedQuery<DiagnosticsStatusByClassReportQuery, Record<string, unknown>>;
  queryDiagnosticStatusByTargetGoupReportRef: PreloadedQuery<
    DiagnosticsStatusByTargetGroupReportQuery,
    Record<string, unknown>
  >;
}

/**
 * Reads whichever diagnostic status report matches the active filter.
 *
 * A site admin filtering by roadmap gets the target-group report, everyone else gets
 * the by-class one. The two share an identical row shape but live under different field
 * names, so we hand back a single value and let the rest of the flow (chart wrapper,
 * chartHelper, nudges) treat both the same.
 *
 * The branch guards a hook call, which is only safe because *both* sides call exactly
 * one `usePreloadedQuery` — the hook order is identical either way, so a site admin
 * toggling between the roadmap and classes filters doesn't desync React's hook list.
 */
export const useDiagnosticStatusReport = ({
  isSiteAdmin,
  isFilteredByRoadmap,
  queryDiagnosticStatusByClassReportRef,
  queryDiagnosticStatusByTargetGoupReportRef,
}: Params): ClassDiagnosticStatusReport => {
  if (isSiteAdmin && isFilteredByRoadmap) {
    return usePreloadedQuery(getDiagnosticStatusByTargetGroupReport, queryDiagnosticStatusByTargetGoupReportRef)
      .targetGroupDiagnosticStatusReport;
  }

  return usePreloadedQuery(getDiagnosticStatusByClassReport, queryDiagnosticStatusByClassReportRef)
    .classDiagnosticStatusReport;
};
